/**
 * Audit field catalog SoT — generated Mongoose scan + manual overrides.
 * Keys are camelCase model names (problem, serviceCatalogItem).
 */

import { AUDIT_FIELD_CATALOG_GENERATED } from './audit-field-catalog.generated.js';
import {
    AUDIT_FIELD_CATALOG_OVERRIDES,
    AUDIT_REF_MODEL_ALIASES,
} from './audit-field-catalog.overrides.js';

export const AUDIT_FIELD_KINDS = Object.freeze([
    'text',
    'enum',
    'ref',
    'refList',
    'date',
    'boolean',
    'number',
    'json',
]);

function mergeField(base, override) {
    if (!override) return { ...base };
    const merged = { ...(base || { kind: 'text' }), ...override };
    if (Array.isArray(override.values)) merged.values = [...override.values];
    return merged;
}

function mergeCatalog(generated, overrides) {
    const out = {};
    const keys = new Set([
        ...Object.keys(generated || {}),
        ...Object.keys(overrides || {}),
    ]);
    for (const entityKey of keys) {
        const baseFields = generated?.[entityKey] || {};
        const overrideFields = overrides?.[entityKey] || {};
        const fieldKeys = new Set([...Object.keys(baseFields), ...Object.keys(overrideFields)]);
        const fields = {};
        for (const fieldKey of fieldKeys) {
            fields[fieldKey] = mergeField(baseFields[fieldKey], overrideFields[fieldKey]);
        }
        out[entityKey] = Object.freeze(fields);
    }
    return Object.freeze(out);
}

export const AUDIT_FIELD_CATALOG = mergeCatalog(
    AUDIT_FIELD_CATALOG_GENERATED.catalog,
    AUDIT_FIELD_CATALOG_OVERRIDES,
);

export const AUDIT_ENTITY_ALIASES = Object.freeze({
    ...(AUDIT_FIELD_CATALOG_GENERATED.aliases || {}),
});

export { AUDIT_REF_MODEL_ALIASES };

/**
 * @param {string|null|undefined} entityType
 * @returns {string|null}
 */
export function normalizeAuditEntityType(entityType) {
    if (entityType == null) return null;
    const raw = String(entityType).trim();
    if (!raw) return null;
    if (AUDIT_FIELD_CATALOG[raw]) return raw;
    const alias = AUDIT_ENTITY_ALIASES[raw] || AUDIT_ENTITY_ALIASES[raw.toLowerCase()];
    if (alias && AUDIT_FIELD_CATALOG[alias]) return alias;
    const camel = raw.charAt(0).toLowerCase() + raw.slice(1);
    if (AUDIT_FIELD_CATALOG[camel]) return camel;
    return camel || null;
}

/**
 * @param {string|null|undefined} entityType
 * @returns {Record<string, { kind: string, refEntity?: string, values?: string[], labelKey?: string }>}
 */
export function getAuditFieldCatalog(entityType) {
    const key = normalizeAuditEntityType(entityType);
    return (key && AUDIT_FIELD_CATALOG[key]) || {};
}

/**
 * @param {string|null|undefined} entityType
 * @param {string} fieldKey
 */
export function getAuditFieldMeta(entityType, fieldKey) {
    const raw = String(fieldKey || '').trim();
    if (!raw) return { kind: 'text' };
    if (raw === 'customFields' || raw.startsWith('customFields.')) {
        return { kind: 'json', custom: true };
    }
    const catalog = getAuditFieldCatalog(entityType);
    return catalog[raw] || { kind: 'text' };
}

/**
 * @param {{ kind?: string }|null|undefined} meta
 */
export function isAuditRefField(meta) {
    return meta?.kind === 'ref' || meta?.kind === 'refList';
}

export function listAuditCatalogEntities() {
    return Object.keys(AUDIT_FIELD_CATALOG).sort();
}
